<script>
    const PAYMENT_STATES = {
        initial: "initial",
        success: "success",
        failed: "failed",
    };

    import { mapState, mapActions } from "vuex";
    import { metaInfo } from "@/core/MetaInfo";
    import BaseButton from "@main/packages/BaseButton";

    export default {
        name: "OnlinePayment",
        components: { BaseButton },
        data() {
            return {
                paymentState: "",
                titleText: "Онлайн-оплата заказа",
                PAYMENT_STATES: Object.freeze(PAYMENT_STATES),
            };
        },
        computed: {
            ...mapState("Root", ["onlinePayment"]),
        },
        serverPrefetch() {
            const orderId = this.$route.params.orderId || this.$route.query.orderId;
            return this.getOnlinePaymentStatus({ orderId });
        },
        mounted() {
            if (!this.onlinePayment?.success) {
                this.$router.push({ name: "Home" });
                return;
            }

            if (this.onlinePayment?.order?.paid) {
                this.paymentState = PAYMENT_STATES.success;
                this.titleText = "Заказ уже оплачен";
                return;
            }

            if (!this.onlinePayment?.order?.paid) {
                this.createCloudPaymentsAgent();
            }
        },
        beforeDestroy() {
            this.destroyCloudPaymentsAgent();
        },
        methods: {
            ...mapActions("Root", ["getOnlinePaymentStatus"]),
            openCloudPaymentsWidget() {
                if (typeof window._startPayMadRobots === "function") {
                    window._startPayMadRobots();
                }
            },
            openStatusOrderModal() {
                this.$popup.show("modal-status-order", {
                    statusOrderId: this.onlinePayment?.order?.number,
                });
            },
            createCloudPaymentsAgent() {
                const script = document.createElement("script");
                script.async = true;
                script.defer = true;
                script.dataset.name = "cloudpayments-agent";
                script.src = "https://widget.cloudpayments.ru/bundles/cloudpayments";
                document.getElementsByTagName("head")[0].appendChild(script);

                const { number, date, buyer, totalPrice, publicKey } =
                    this.onlinePayment.order;

                script.onload = () => {
                    window._startPayMadRobots = () => {
                        let widget = new window.cp.CloudPayments({ language: "ru-RU" });
                        widget.pay(
                            "charge",
                            {
                                publicId: publicKey,
                                description: `заказ № ${number} от ${date}`,
                                accountId: buyer,
                                invoiceId: number,
                                amount: Number(totalPrice),
                                currency: "RUB",
                            },
                            {
                                onSuccess: () => {
                                    this.paymentState = PAYMENT_STATES.success;
                                    this.titleText = "Оплата прошла успешно!";
                                },
                                onFail: () => {
                                    this.paymentState = PAYMENT_STATES.failed;
                                    this.titleText = "Произошла ошибка платежа!";
                                },
                            }
                        );
                    };
                    window._startPayMadRobots();
                };
            },
            destroyCloudPaymentsAgent() {
                const script = document.head.querySelector("[data-name=cloudpayments-agent]");
                if (script) {
                    document.head.removeChild(script);
                }
            },
        },
        metaInfo() {
            return metaInfo.getMeta("online-payment");
        },
    };
</script>

<template>
    <div class="online-payment">
        <div class="l-container">
            <h2
                class="online-payment__title"
                :class="{
                    'is-success': paymentState === PAYMENT_STATES.success,
                    'is-failed': paymentState === PAYMENT_STATES.failed,
                }"
            >
                {{ titleText }}
            </h2>

            <div class="online-payment__wrapper">
                <div
                    v-if="paymentState === PAYMENT_STATES.success"
                    class="online-payment__section"
                >
                    <base-button
                        class="online-payment__button"
                        size="lg"
                        @click="openStatusOrderModal"
                    >
                        Узнать статус заказа
                    </base-button>
                </div>

                <div
                    v-if="paymentState === PAYMENT_STATES.failed"
                    class="online-payment__section"
                >
                    <div class="online-payment__text">
                        Ваш платеж по какой-то причине не был проведен. <br />
                        Попробуйте оплатить еще раз
                    </div>
                    <base-button
                        class="online-payment__button"
                        size="lg"
                        @click="openCloudPaymentsWidget"
                    >
                        Повторить платеж
                    </base-button>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    .online-payment {
        min-height: 50vh;
        padding: 56px 0 60px;
        text-align: center;
    }

    .online-payment__wrapper {
        display: flex;
        justify-content: center;
    }

    .online-payment__title {
        margin-bottom: 16px;
        font-family: var(--typography-heading-font);
        font-size: 26px;
        font-weight: var(--typography-heading-weight);
        line-height: 40px;
        text-transform: var(--typography-heading-case);
        &.is-success {
            color: var(--success);
        }
        &.is-failed {
            color: var(--danger);
        }
        @include bp($bp-desktop-sm) {
            margin-bottom: 32px;
            font-size: 36px;
            line-height: 40px;
        }
    }

    .online-payment__section {
        width: 100%;
        max-width: 520px;
    }

    .online-payment__text {
        margin-bottom: 16px;
        color: var(--typography-text-color);
        font-size: 14px;
        font-weight: var(--typography-text-weight);
        line-height: 28px;
        @include bp($bp-desktop-sm) {
            margin-bottom: 32px;
            font-size: 16px;
            line-height: 32px;
        }
    }

    .online-payment__button {
        width: 100%;
        max-width: 280px;
        margin: 0 auto;
    }
</style>
